import React, { useMemo } from 'react'
import { AIEvaluation as AIEvaluationType } from 'types/campaign/applications'
import './style.scss'

const CheckCircleIcon = () => (
    <svg
        className="ai-evaluation-pitch__icon"
        width={16}
        height={16}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.3181 4.3181 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.3181 14.6667 8ZM10.6869 5.97978C10.8821 6.17504 10.8821 6.49162 10.6869 6.68689L7.35355 10.0202C7.15829 10.2155 6.84171 10.2155 6.64645 10.0202L5.31311 8.68689C5.11785 8.49162 5.11785 8.17504 5.31311 7.97978C5.50838 7.78452 5.82496 7.78452 6.02022 7.97978L7 8.95956L8.48989 7.46967L9.97978 5.97978C10.175 5.78452 10.4916 5.78452 10.6869 5.97978Z"
            fill="#0BAC68"
        />
    </svg>
)

const CloseCircleIcon = () => (
    <svg
        className="ai-evaluation-pitch__icon"
        width={16}
        height={16}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.3181 4.3181 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.3181 14.6667 8ZM5.97975 5.97977C6.17501 5.7845 6.4916 5.7845 6.68686 5.97977L7.99998 7.29289L9.31308 5.97978C9.50835 5.78452 9.82493 5.78452 10.0202 5.97978C10.2155 6.17504 10.2155 6.49162 10.0202 6.68689L8.70709 7.99999L10.0202 9.31309C10.2154 9.50835 10.2154 9.82493 10.0202 10.0202C9.82492 10.2155 9.50833 10.2155 9.31307 10.0202L7.99998 8.7071L6.68687 10.0202C6.49161 10.2155 6.17503 10.2155 5.97977 10.0202C5.7845 9.82494 5.7845 9.50836 5.97977 9.3131L7.29287 7.99999L5.97975 6.68687C5.78449 6.49161 5.78449 6.17503 5.97975 5.97977Z"
            fill="#FF343E"
        />
    </svg>
)

const SparkleIcon = () => (
    <svg
        className="ai-evaluation__sparkle"
        width={16}
        height={16}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            fill="#EF4444"
            d="m8 .5 1.05 5.45L14.5 8l-5.45 2.05L8 15.5 6.95 10.05 1.5 8l5.45-2.05L8 .5Z"
        />
    </svg>
)

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
    <div className="ai-evaluation__section-title">
        <SparkleIcon />
        <h3 className="ai-evaluation__section-heading">{children}</h3>
    </div>
)

const AIEvaluation = ({ evaluation }: { evaluation: AIEvaluationType['evaluation'] }) => {
    const isRejected = evaluation.final_verdict?.recommendation === 'REJECT'

    const pitch = evaluation.final_verdict?.pitch_statement?.trim() ?? ''

    const strengths = useMemo(
        () => (evaluation.strategic_analysis?.strengths ?? []).filter((s) => Boolean(s?.trim())),
        [evaluation.strategic_analysis?.strengths]
    )

    const weaknesses = useMemo(
        () => (evaluation.strategic_analysis?.weaknesses ?? []).filter((s) => Boolean(s?.trim())),
        [evaluation.strategic_analysis?.weaknesses]
    )

    const pitchTitle = isRejected
        ? "Why this creator is not recommended"
        : 'Why you should hire this creator'

    const hasEvaluationDetails =
        (!isRejected && strengths.length > 0) || (isRejected && weaknesses.length > 0)

    const hasAnyContent =
        Boolean(pitch) || hasEvaluationDetails

    if (!hasAnyContent) {
        return null
    }

    return (
        <>
        {hasEvaluationDetails ? (
        <div className="ai-evaluation" role="region" aria-label="AI evaluation summary">
            {!isRejected && strengths.length > 0 ? (
                <section className="ai-evaluation__section">
                    <SectionHeader>What Makes Them Valuable</SectionHeader>
                    <ul className="ai-evaluation__list">
                        {strengths.map((item, idx) => (
                            <li key={`strength-${idx}`} className="ai-evaluation__list-item">
                                {item}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {isRejected && weaknesses.length > 0 ? (
                <section className="ai-evaluation__section">
                    <SectionHeader>Issues Identified</SectionHeader>
                    <ul className="ai-evaluation__list">
                        {weaknesses.map((item, idx) => (
                            <li key={`weak-${idx}`} className="ai-evaluation__list-item">
                                {item}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
        </div>
        ) : null}
        {pitch ? (
            <div
                className={`ai-evaluation-pitch${isRejected ? ' ai-evaluation-pitch--rejected' : ''}`}
                role="region"
                aria-label={pitchTitle}
            >
                <div className="ai-evaluation-pitch__header">
                    {isRejected ? <CloseCircleIcon /> : <CheckCircleIcon />}
                    <h3 className="ai-evaluation-pitch__title">{pitchTitle}</h3>
                </div>
                <p className="ai-evaluation-pitch__body">{pitch}</p>
            </div>
        ) : null}
        </>
    )
}

export default AIEvaluation
